/**
 * หน้าจอตารางสิทธิ์ตามแผนก — เลือกแผนก แล้วแก้สี่ flag ของทุกโมดูล
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับที่ `WO-P1-M10-001` วางไว้: อ่าน flag จาก `modules`
 * ของ permission set ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ผ่าน `can()` **ห้าม derive กติกา
 * ADMIN/AUDITOR ซ้ำจาก `system_roles`** และทุกสถานะที่ไม่ใช่ `ready` แปลว่าไม่มีสิทธิ์
 *
 * `RBAC_ADMIN` + `VIEW` เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route ตาม
 * `action` ของรายการเมนู) ส่วน `RBAC_ADMIN` + `EDIT` เป็นสิทธิ์ของ **การบันทึก**
 * ซึ่งถูกบังคับข้างในหน้าจอตรงที่ของมัน เหมือนที่ฟอร์มรับของเข้าบังคับ
 * `INVENTORY` + CREATE เอง — หลัง D-060 มีเพียง `ADMIN` ที่ถือ `RBAC_ADMIN` เลย
 * ในทางปฏิบัติจึงมีแต่ ADMIN ที่มาถึงหน้านี้ แต่หน้าจอไม่ได้สรุปเช่นนั้นเอง มันอ่าน
 * flag ที่ได้รับมา
 *
 * **สิ่งที่หน้าจอนี้ไม่มีโดยเจตนา**: สร้างแผนก, จัดการพนักงาน, มอบ/ถอด role และ
 * บันทึกการเปลี่ยนสิทธิ์ — ทั้งสามเป็นหน้าจอของตัวเองใน M10 การยัดรวมมาที่นี่จะได้
 * หน้าจอที่ไม่มีใครทดสอบครบสักส่วน
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย การซ่อนปุ่มบันทึกไม่ได้ป้องกัน
 * อะไรเลย — เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 */

import { useEffect, useState } from 'react';

import { DepartmentPermissionGrid } from './DepartmentPermissionGrid';
import { RbacAdminFailureNotice } from './RbacAdminFailureNotice';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchDepartments, type RbacAdminFailure } from '../../lib/rbacAdmin/client';
import { RBAC_ADMIN_MODULE, type Department } from '../../lib/rbacAdmin/types';

export interface PermissionMatrixScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type DepartmentsState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Department[] }
  | RbacAdminFailure;

export function PermissionMatrixScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: PermissionMatrixScreenProps) {
  const canEdit = can(permissions, RBAC_ADMIN_MODULE, 'EDIT');

  const [state, setState] = useState<DepartmentsState>({ status: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchDepartments({ apiBaseUrl, fetchImpl, signal: controller.signal }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      if (result.status === 'ready') {
        // เลือกแผนกแรกให้เลย (รายการเรียงตามชื่อแผนกมาแล้วจากเซิร์ฟเวอร์) —
        // หน้าจอที่เปิดมาแล้วว่างเปล่าจนกว่าจะเลือกอะไรสักอย่าง ทำให้ผู้ดูแลเดาว่า
        // ยังโหลดไม่เสร็จ ส่วนแผนกที่ไม่มีเลยมีสถานะของตัวเองอยู่แล้วด้านล่าง
        setSelected(result.page.items[0]?.department_id ?? null);
        setState({ status: 'ready', items: result.page.items });
        return;
      }
      setSelected(null);
      setState(result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const departments = state.status === 'ready' ? state.items : [];
  const selectedDepartment = departments.find(
    (department) => department.department_id === selected,
  );

  return (
    <div className="cap-page" data-testid="rbac-permission-matrix">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ตารางสิทธิ์ตามแผนก{' '}
            <InfoHint
              label="เกี่ยวกับตารางสิทธิ์ตามแผนก"
              text="สิทธิ์เป็นสองมิติที่อิสระต่อกัน (D-008): โรงงานที่เข้าถึงได้เป็นคนละคำถามกับ โมดูล × การกระทำที่ทำได้ — ตารางนี้ตอบคำถามที่สอง และมอบสิทธิ์กลุ่ม System Administration ให้ใครไม่ได้เลย (D-060)"
            />
          </h1>
        </div>
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="rbac-matrix-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังแก้ตารางสิทธิ์ไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
            ของโมดูล <code className="cap-table__code">{RBAC_ADMIN_MODULE}</code>{' '}
            ช่องติ๊กทั้งตารางจึงถูกปิดไว้และไม่มีปุ่มบันทึก
          </p>
          <p className="cap-notice__note">
            การปิดช่องเป็นเพียง UX เพื่อไม่ให้ตั้งค่าที่จะถูกปฏิเสธตอนบันทึก —
            ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="rbac-departments-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการแผนก…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <RbacAdminFailureNotice
          failure={state}
          context="departments"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="rbac-departments-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีแผนก</StatusBadge>
          <h2 className="cap-notice__title">องค์กรนี้ยังไม่มีแผนกให้ตั้งสิทธิ์</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการแผนกว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
            สิทธิ์ผูกกับแผนกเสมอ จึงต้องสร้างแผนกก่อนจึงจะมีอะไรให้ตั้งค่าในตารางนี้
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <form className="cap-filters" aria-label="เลือกแผนกที่จะตั้งสิทธิ์">
          <label className="cap-field">
            <span className="cap-field__label">แผนก</span>
            <select
              className="cap-input"
              name="department_id"
              data-testid="rbac-department-select"
              value={selected ?? ''}
              onChange={(event) => {
                setSelected(event.target.value);
              }}
            >
              {state.items.map((department) => (
                <option key={department.department_id} value={department.department_id}>
                  {department.department_name}
                </option>
              ))}
            </select>
            <InfoHint
              label="เกี่ยวกับช่องเลือกแผนก"
              text="ตารางสิทธิ์เป็นรายแผนกเสมอ ไม่มีมุมมองรวมทุกแผนก — และไม่ผูกกับโรงงานใด เพราะเป็นการตั้งค่าระดับองค์กร"
            />
          </label>
        </form>
      ) : null}

      {selectedDepartment === undefined ? null : (
        <DepartmentPermissionGrid
          key={selectedDepartment.department_id}
          departmentId={selectedDepartment.department_id}
          departmentName={selectedDepartment.department_name}
          apiBaseUrl={apiBaseUrl}
          fetchImpl={fetchImpl}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
